import { SearchParams } from '@api/constants';
import { getClientUserSession, getTenantInfo } from '@api/portalServices';
import type { TenantInfoResponse, UserInfo } from '@api/types';
import { type ErrorCode, SESSION_ERROR_CODES } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { QueryKey } from '@portal/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { initializePendo } from '@utils/pendo';
import { initTracerPortal } from '@utils/tracer';
import { getIdpLookupUrl } from '@utils/utils';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

export type SessionState = {
  tenantId?: string;
  tenantType?: string;
  workflowId?: string;
  userInfo?: UserInfo;
  tenantInfo?: TenantInfoResponse;
};

export const SessionContext = createContext<
  (SessionState & { clearSession: () => void; refreshSession: () => Promise<void> }) | null
>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionContext');
  }
  return context;
};

export function isCurrentUser(username?: string, sessionUserInfo?: UserInfo) {
  return (
    sessionUserInfo &&
    (username === sessionUserInfo.userDisplayName || username === sessionUserInfo.userUpn)
  );
}

export const Session = ({ children }: PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Save the error on notification & Handle 401 errors and clearSession
  const { error, clearErrorNotifications } = useErrorContext();

  const {
    data: sessionData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.CLIENT_USER_SESSION],
    queryFn: async () => {
      // Do not use emitError here - will cause endless loop!
      try {
        const wid = uuidv4();
        initTracerPortal(wid); //TODO revisit this - causing issues within react query
        const stateParam = searchParams.get(SearchParams.STATE);
        const tenantIdForLookup = stateParam?.trim() || undefined;
        const response = await getClientUserSession('', tenantIdForLookup);

        if (response) {
          const tenantInfo = await getTenantInfo(response.tenantId || '');
          initializePendo(response.tenantId, response.userInfo);
          return {
            tenantId: response.tenantId,
            tenantType: response.tenantType,
            userInfo: response.userInfo,
            workflowId: wid,
            tenantInfo,
          };
        }
      } catch (e) {
        console.log('Error fetching user session:', e);
      }
      return null;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const path = window.location.pathname;
    let stateParam: string | null = null;
    /* During the EntraId configuration, we redirect to Microsoft for authentication.
    After a successful authentication, on redirect to portal we receive the tenantId in a query param named - "state".
    We have to read this and add a tenantId query param for all portal flows to work well,
    especially primary/test tenant scenarios. */
    if (path === '/entraid/auth-success') {
      //TODO check if we still need this
      stateParam = searchParams.get(SearchParams.STATE);
      if (stateParam) {
        searchParams.set(SearchParams.TENANT_ID, stateParam);
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, setSearchParams]);

  const clearSession = useCallback(() => {
    if (searchParams?.get(SearchParams.TENANT_ID)) {
      searchParams.delete(SearchParams.TENANT_ID);
      setSearchParams(searchParams);
    }
    queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_USER_SESSION], exact: true });
  }, [searchParams, setSearchParams, queryClient]);

  const refreshSession = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const contextValue = useMemo(
    () => ({ ...sessionData, clearSession, refreshSession }),
    [clearSession, refreshSession, sessionData],
  );

  useEffect(() => {
    if (SESSION_ERROR_CODES.includes(error?.code as ErrorCode)) {
      console.debug('Session clearSession.');
      clearErrorNotifications();
      clearSession();
    }
  }, [error, clearErrorNotifications, clearSession]);

  if (isFetching) {
    return null;
  }

  if (!sessionData || !sessionData.tenantId) {
    const redirectUrl = getIdpLookupUrl();
    const disableAutoRedirect =
      import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_DISABLE_AUTO_REDIRECT === 'true';

    if (disableAutoRedirect) {
      console.info('[am-portal local-redirect] auto redirect disabled', { redirectUrl });
      return null;
    }

    console.warn('No session (bad). Redirecting: ', redirectUrl);

    window.location.href = redirectUrl;

    return 'Redirecting to identity provider...';
  }

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
